import { useGT } from 'gt-react';
import { Separator } from 'src/components/ui/separator';
import {
  useForecastFactors,
  useRevokeForecastFactor,
  type ForecastFactorRow,
} from 'src/api/rpc/hooks/procurement/useForecastFactors';
import { OverrideCountBadges } from '../components/OverrideCountBadges';
import {
  OverrideRevokeDialog,
  useOverrideRevoke,
} from '../components/OverrideRevoke';
import { formatFactorPercent } from '../components/forecastFactorFormat';
import { ForecastFactorHistory } from './ForecastFactorHistory';
import { ForecastFactorForm } from './ForecastFactorForm';
import { useForecastFactorForm } from './useForecastFactorForm';
import { buildFactorSchedule } from './forecastFactorSchedule';
import { ForecastFactorTimeline } from './ForecastFactorTimeline';

interface ForecastFactorPanelProps {
  warehouseId: string;
  /** ProductV3 key (the SKU), authoritative. */
  productV3Key?: string | null;
}

// Factors are ProductV3-only: a line with no SKU has no forecast to scale, and
// gating here rather than inside the panel keeps the series identity
// non-nullable for everything below.
export function ForecastFactorPanel({
  warehouseId,
  productV3Key,
}: ForecastFactorPanelProps) {
  const gt = useGT();
  if (!productV3Key) {
    return (
      <p className="text-xs text-gray-400">
        {gt('No forecast to scale for this line (no product reference).', {
          context: 'growth factor panel empty state',
        })}
      </p>
    );
  }
  return (
    <SeriesFactorPanel warehouseId={warehouseId} productV3Key={productV3Key} />
  );
}

function SeriesFactorPanel({
  warehouseId,
  productV3Key,
}: {
  warehouseId: string;
  productV3Key: string;
}) {
  const gt = useGT();
  const identity = { warehouseId, productV3Key };
  const list = useForecastFactors(identity);
  const revokeMutation = useRevokeForecastFactor();
  const form = useForecastFactorForm(identity);
  const revoke = useOverrideRevoke<ForecastFactorRow>({
    revoke: (o, handlers) =>
      revokeMutation.mutate({ overrideId: o.id }, handlers),
    pending: revokeMutation.isPending,
    successMessage: gt('Growth factor revoked', {
      context: 'growth factor toast',
    }),
    // The row being edited just stopped existing; drop the edit with it.
    onRevoked: (o) => {
      if (form.editing?.id === o.id) form.reset();
    },
  });

  const overrides = list.data?.overrides ?? [];
  const schedule = buildFactorSchedule(overrides, new Date());
  const overrideCount = overrides.filter((o) => o.isActive).length;
  const needsReviewCount = overrides.filter((o) => o.needsReview).length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {gt('Growth / shrinkage factor', {
            context: 'growth factor panel heading',
          })}
        </h4>
        <OverrideCountBadges
          overrideCount={overrideCount}
          needsReviewCount={needsReviewCount}
        />
      </div>

      {list.isError ? (
        <p className="text-xs text-destructive">
          {gt('Could not load growth adjustments.', {
            context: 'growth factor schedule error',
          })}
        </p>
      ) : list.data ? (
        <ForecastFactorTimeline periods={schedule} />
      ) : (
        <p className="text-xs text-muted-foreground">
          {gt('Loading growth adjustments...', {
            context: 'growth factor schedule loading',
          })}
        </p>
      )}

      <ForecastFactorHistory
        overrides={overrides}
        schedule={schedule}
        onEdit={form.startEdit}
        onRequestRevoke={revoke.request}
      />

      <Separator className="my-2" />

      <ForecastFactorForm form={form} />

      <p className="mt-1 text-xs text-gray-400">
        {gt(
          'Scales the forecast for this item at this warehouse over the window; safety stock and reorder point follow on save. +15 plans 15% above the forecast, -10 plans 10% below.',
          { context: 'growth factor help text' },
        )}
      </p>

      <OverrideRevokeDialog
        revoke={revoke}
        title={gt('Revoke growth factor?', {
          context: 'revoke growth factor confirm title',
        })}
        message={
          revoke.target
            ? gt(
                'The {pct} factor stops applying now. The forecast returns to the statistical curve (or the next active factor) and the levels are recomputed.',
                {
                  context: 'revoke growth factor confirm message',
                  pct: formatFactorPercent(revoke.target.factor),
                },
              )
            : undefined
        }
      />
    </div>
  );
}
